/**
 * The falcon, and the other things that start an agitation wave.
 *
 * Replay mode: a deterministic schedule from the scene seed. Attacks come in
 * bouts of one to four passes: a stoop from high and to one side, fastest at
 * the flock, pulling up into a climb, then circling above before the next
 * pass; about a third of passes are level tail-chases instead. Between bouts
 * the falcon is gone. Point startles, also seeded, fire a few birds now and
 * then so waves happen without a visible cause, as in real roosts.
 *
 * Live mode: the falcon chases the pointer's world target with a speed and
 * turn limit, so it flies a bird-like path instead of teleporting. Live wins
 * while present; it is transient and never encoded in the permalink.
 *
 * Everything is integer-hash based, so schedules are bit-identical across
 * JavaScript engines. Positions use Math.sin/cos and may differ by an ulp,
 * which is invisible.
 */

export type Vec3 = [number, number, number];

export interface PredatorContext {
  anchor: Vec3;
  /** Roost radius, metres. */
  spread: number;
  /** Where the birds actually are right now; the anchor when unknown. */
  center: Vec3;
  /** 0..1 from the scene parameters. */
  rate: number;
  aggression: number;
  nervousness: number;
}

export type FalconMode = 'idle' | 'stoop' | 'chase' | 'circle' | 'leaving' | 'live';

export interface PredatorState {
  /** True while the falcon influences the flock. */
  active: boolean;
  /** True while a falcon should be drawn. */
  visible: boolean;
  pos: Vec3;
  vel: Vec3;
  /** Force radius in metres, 0 when inactive. Seeding uses 70 % of it. */
  radius: number;
  phase: number;
  /** 0 flapping .. 1 gliding. */
  glide: number;
  /** Bank angle, radians. */
  roll: number;
  /** 0 wings out .. 1 tucked for the stoop. */
  fold: number;
  mode: FalconMode;
  /** Seconds until the next bout, or null when scheduling is off or a bout runs. */
  nextIn: number | null;
  live: boolean;
  /** Whole-flock alarm, 0..1, and where the last attack came from. */
  alarm: number;
  alarmFrom: Vec3;
  /** Seeded point startle: xyz and radius, radius 0 when none. */
  startle: [number, number, number, number];
}

/** splitmix32: exact in JS integer arithmetic. */
export function hash32(a: number, b: number, c: number): number {
  let h = (a ^ Math.imul(b, 0x9e3779b1) ^ Math.imul(c, 0x85ebca77)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x21f0aaad);
  h = Math.imul(h ^ (h >>> 15), 0x735a2d97);
  h = (h ^ (h >>> 15)) >>> 0;
  return h;
}

/** Uniform in [0, 1). */
function unit(seed: number, i: number, k: number): number {
  return hash32(seed, i, k) / 4294967296;
}

interface Pass {
  kind: 'stoop' | 'chase';
  /** Control points of a cubic Bézier, relative to the flock centre so the pass tracks a moving flock. */
  p: [Vec3, Vec3, Vec3, Vec3];
  /** Arc length table: cumulative length at 64 samples, and the total. */
  arc: Float64Array;
  length: number;
  /** Speeds along the pass, m/s. */
  vTop: number;
  vBottom: number;
  /** Arc fraction of the closest approach. */
  bottomAt: number;
  radius: number;
  exitDir: Vec3;
}

interface Bout {
  passes: number;
  passIndex: number;
  pass: Pass | null;
  /** Arc length travelled along the current pass. */
  s: number;
  /** Circling between passes, at `height` above the flock centre. */
  circle: { height: number; angle: number; until: number } | null;
  /** Leaving after the last pass. */
  leaveUntil: number;
}

const FLAP_HZ = 4.5;
const LIVE_MAX_SPEED = 70;
const LIVE_MAX_TURN = 3; // rad/s

export class Predator {
  private seed = 0;
  private boutIndex = 0;
  private bout: Bout | null = null;
  private nextBoutStart = Number.POSITIVE_INFINITY;
  private lastRate = -1;

  private pos: Vec3 = [0, 0, 0];
  private vel: Vec3 = [0, 0, 0];
  private prevVel: Vec3 = [0, 0, 0];
  private radius = 0;
  private phase = 0;
  private glide = 0;
  private roll = 0;
  private fold = 0;
  private mode: FalconMode = 'idle';
  private visibleFor = 0;

  private liveTarget: Vec3 | null = null;
  private wasLive = false;
  private liveLeaveUntil = 0;
  private leavingFromLive = false;
  /** Position along the current pass relative to the flock centre, so velocity excludes the centre's own jumps. */
  private rel: Vec3 = [0, 0, 0];

  private alarm = 0;
  private alarmFrom: Vec3 = [0, 0, 0];

  private startleIndex = 0;
  private nextStartle = Number.POSITIVE_INFINITY;
  private lastNerv = -1;
  private startle: [number, number, number, number] = [0, 0, 0, 0];
  private startleUntil = 0;

  reset(seed: number): void {
    this.seed = seed >>> 0;
    this.boutIndex = 0;
    this.bout = null;
    this.nextBoutStart = Number.POSITIVE_INFINITY;
    this.lastRate = -1;
    this.radius = 0;
    this.mode = 'idle';
    this.visibleFor = 0;
    this.alarm = 0;
    this.alarmFrom = [0, 0, 0];
    this.startleIndex = 0;
    this.nextStartle = Number.POSITIVE_INFINITY;
    this.lastNerv = -1;
    this.startle = [0, 0, 0, 0];
    this.startleUntil = 0;
    this.liveLeaveUntil = 0;
    this.wasLive = false;
    this.leavingFromLive = false;
    this.pos = [0, 0, 0];
    this.vel = [0, 0, 0];
    this.prevVel = [0, 0, 0];
    this.fold = 0;
    this.phase = 0;
    this.roll = 0;
    this.glide = 0;
  }

  /** World position under the finger or cursor, or null when released. */
  setLive(target: Vec3 | null): void {
    this.liveTarget = target;
  }

  step(simTime: number, dt: number, ctx: PredatorContext): PredatorState {
    this.schedule(simTime, ctx);
    this.stepStartles(simTime, dt, ctx);

    if (this.liveTarget) {
      this.stepLive(dt, ctx);
    } else if (this.wasLive) {
      // Released: fly on along the last heading and up while the influence fades.
      this.wasLive = false;
      this.leavingFromLive = true;
      this.liveLeaveUntil = simTime + 1.5;
      this.mode = 'leaving';
    }

    if (!this.liveTarget) {
      if (simTime < this.liveLeaveUntil) {
        this.vel = [this.vel[0], this.vel[1] + 12 * dt, this.vel[2]];
        this.pos = [this.pos[0] + this.vel[0] * dt, Math.max(5, this.pos[1] + this.vel[1] * dt), this.pos[2] + this.vel[2] * dt];
        this.radius = approach(this.radius, 0, dt, 0.6);
      } else {
        this.leavingFromLive = false;
        this.stepScheduled(simTime, dt, ctx);
      }
    }

    // Alarm rises fast while an attack presses the flock and fades slowly after.
    // A finger resting on the flock, or lifting off it, is not an attack.
    const speedNow = Math.hypot(this.vel[0], this.vel[1], this.vel[2]);
    const liveish = this.mode === 'live' || this.leavingFromLive;
    const pressing = this.radius > 0.5 && (!liveish || speedNow > 5);
    if (pressing) {
      this.alarm += (1 - this.alarm) * (1 - Math.exp(-dt / 0.5));
      this.alarmFrom = [this.pos[0], this.pos[1], this.pos[2]];
    } else {
      this.alarm *= Math.exp(-dt / 12);
      if (this.alarm < 0.005) this.alarm = 0;
    }

    // Sprite state.
    const active = this.radius > 0.5;
    const speed = Math.hypot(this.vel[0], this.vel[1], this.vel[2]);
    this.visibleFor = this.mode !== 'idle' ? 1 : Math.max(0, this.visibleFor - dt);
    this.fold = approach(this.fold, smoothstep(30, 45, speed), dt, 0.25);
    const wantGlide = this.fold > 0.5 || this.vel[1] < -3 ? 1 : 0;
    this.glide = approach(this.glide, wantGlide, dt, 0.3);
    this.phase = (this.phase + dt * FLAP_HZ * 2 * Math.PI * (1 - 0.95 * this.glide)) % (2 * Math.PI);
    this.updateRoll(dt, this.mode !== 'idle');

    return {
      active,
      visible: this.mode !== 'idle' || this.visibleFor > 0,
      pos: this.pos,
      vel: this.vel,
      radius: active ? this.radius : 0,
      phase: this.phase,
      glide: this.glide,
      roll: this.roll,
      fold: this.fold,
      mode: this.mode,
      nextIn: this.bout || !Number.isFinite(this.nextBoutStart) ? null : Math.max(0, this.nextBoutStart - simTime),
      live: this.liveTarget !== null,
      alarm: this.alarm,
      alarmFrom: this.alarmFrom,
      startle: simTime < this.startleUntil ? this.startle : [0, 0, 0, 0],
    };
  }

  // ---- schedule ------------------------------------------------------------

  private schedule(simTime: number, ctx: PredatorContext): void {
    const on = ctx.rate > 0.02;
    if (!on) {
      this.lastRate = -1;
      this.nextBoutStart = Number.POSITIVE_INFINITY;
      return;
    }
    if (this.lastRate !== ctx.rate && !this.bout) {
      // First schedule, or the rate slider moved. A bout under way is never touched.
      const first = this.boutIndex === 0 && this.lastRate < 0;
      this.lastRate = ctx.rate;
      this.nextBoutStart = simTime + (first ? lerp(45, 20, ctx.rate) * (0.7 + 0.6 * unit(this.seed, 0, 1)) : this.boutGap(ctx.rate));
    }
  }

  /** Seconds between bouts: 150 s at the lowest rate down to 25 s, jittered ±40 %. */
  private boutGap(rate: number): number {
    return lerp(150, 25, rate) * (0.6 + 0.8 * unit(this.seed, this.boutIndex, 2));
  }

  private stepScheduled(simTime: number, dt: number, ctx: PredatorContext): void {
    if (!this.bout) {
      if (simTime >= this.nextBoutStart) {
        const passes = 1 + Math.floor(unit(this.seed, this.boutIndex, 3) * (1 + 3 * ctx.aggression));
        this.bout = { passes, passIndex: 0, pass: this.makePass(this.boutIndex, 0, ctx), s: 0, circle: null, leaveUntil: 0 };
        this.rel = this.bout.pass!.p[0];
        this.pos = add(ctx.center, this.rel);
        this.vel = [0, 0, 0];
        this.prevVel = [0, 0, 0];
        this.radius = 0;
        this.mode = this.bout.pass!.kind;
      } else {
        this.mode = 'idle';
        this.radius = 0;
        // Still drawn for a moment after a bout or a release: keep flying rather than hang in the air.
        if (this.visibleFor > 0) {
          this.pos = [this.pos[0] + this.vel[0] * dt, Math.max(5, this.pos[1] + this.vel[1] * dt), this.pos[2] + this.vel[2] * dt];
        }
        return;
      }
    }
    const b = this.bout;

    if (b.pass) {
      const pass = b.pass;
      const frac = pass.length > 0 ? b.s / pass.length : 1;
      const bell = Math.exp(-Math.pow((frac - pass.bottomAt) / 0.28, 2));
      const speed = lerp(pass.vTop, pass.vBottom, bell);
      b.s += speed * dt;
      if (b.s >= pass.length) {
        // Pass done: circle for the next one, or leave.
        b.passIndex++;
        b.pass = null;
        if (b.passIndex < b.passes) {
          const dur = lerp(5, 10, unit(this.seed, this.boutIndex * 16 + b.passIndex, 4));
          const height = lerp(60, 100, unit(this.seed, this.boutIndex * 16 + b.passIndex, 5));
          const angle = Math.atan2(this.pos[2] - ctx.center[2], this.pos[0] - ctx.center[0]);
          b.circle = { height, angle, until: simTime + dur };
          this.mode = 'circle';
        } else {
          b.leaveUntil = simTime + 3;
          this.mode = 'leaving';
        }
        this.radius = 0;
        return;
      }
      const u = arcToParam(pass.arc, b.s / pass.length);
      const nr = bezier3(pass.p, u);
      // Velocity from the flock-relative path only: the centre readback jumps every few steps.
      this.vel = [(nr[0] - this.rel[0]) / dt, (nr[1] - this.rel[1]) / dt, (nr[2] - this.rel[2]) / dt];
      this.rel = nr;
      const np = add(ctx.center, nr);
      np[1] = Math.max(5, np[1]);
      this.pos = np;
      const ramp = smoothstep(0, 0.08, frac) * (1 - smoothstep(0.88, 1, frac));
      this.radius = pass.radius * ramp;
      this.mode = pass.kind;
      return;
    }

    if (b.circle) {
      const c = b.circle;
      if (simTime >= c.until) {
        b.circle = null;
        b.pass = this.makePass(this.boutIndex, b.passIndex, ctx);
        // Glide from the circle onto the pass start over a moment: snap is fine at 100 m up.
        this.rel = b.pass.p[0];
        this.pos = add(ctx.center, this.rel);
        this.mode = b.pass.kind;
        return;
      }
      const r = 40;
      const w = 15 / r;
      c.angle += w * dt;
      this.vel = [-Math.sin(c.angle) * 15, 0, Math.cos(c.angle) * 15];
      this.pos = [ctx.center[0] + Math.cos(c.angle) * r, ctx.center[1] + c.height, ctx.center[2] + Math.sin(c.angle) * r];
      this.radius = 0;
      this.mode = 'circle';
      return;
    }

    // Leaving: climb out and slow down, then the bout is over.
    if (simTime < b.leaveUntil) {
      const sp = Math.hypot(this.vel[0], this.vel[1], this.vel[2]);
      const target = 15;
      const k = sp > 0.1 ? Math.max(target, sp - 12 * dt) / sp : 1;
      this.vel = [this.vel[0] * k, this.vel[1] * k + 6 * dt, this.vel[2] * k];
      this.pos = [this.pos[0] + this.vel[0] * dt, this.pos[1] + this.vel[1] * dt, this.pos[2] + this.vel[2] * dt];
      this.radius = 0;
      this.mode = 'leaving';
      return;
    }
    this.bout = null;
    this.boutIndex++;
    this.nextBoutStart = simTime + this.boutGap(ctx.rate);
    this.mode = 'idle';
    this.radius = 0;
  }

  private makePass(bout: number, index: number, ctx: PredatorContext): Pass {
    const s = this.seed;
    const i = bout * 16 + index;
    const aggr = ctx.aggression;
    const flockR = Math.max(8, ctx.spread * 0.6);

    // Everything below is relative to the flock centre, which the pass follows.
    // Aim at the flock's surface; timid falcons skim past it, bold ones cut inside.
    const azAim = unit(s, i, 10) * Math.PI * 2;
    const miss = Math.max(0, 0.5 - aggr) * 2 * lerp(5, 15, unit(s, i, 11));
    const inward = aggr * 0.3 * flockR;
    const target: Vec3 = [
      Math.cos(azAim) * (flockR - inward + miss),
      (unit(s, i, 12) - 0.5) * flockR * 0.5,
      Math.sin(azAim) * (flockR - inward + miss),
    ];

    const kind: Pass['kind'] = unit(s, i, 13) < 0.3 ? 'chase' : 'stoop';
    const azIn = unit(s, i, 14) * Math.PI * 2;
    const azOut = azIn + Math.PI * (0.8 + 0.4 * (unit(s, i, 15) - 0.5));
    let p: Pass['p'];
    let vTop: number;
    let vBottom: number;
    if (kind === 'stoop') {
      const side = lerp(80, 160, unit(s, i, 16));
      const start: Vec3 = [
        target[0] + Math.cos(azIn) * side,
        target[1] + flockR * 0.5 + lerp(60, 120, unit(s, i, 17)),
        target[2] + Math.sin(azIn) * side,
      ];
      const end: Vec3 = [target[0] + Math.cos(azOut) * 100, target[1] + 50, target[2] + Math.sin(azOut) * 100];
      p = [
        start,
        [lerp(start[0], target[0], 0.55), lerp(start[1], target[1], 0.85), lerp(start[2], target[2], 0.55)],
        [lerp(target[0], end[0], 0.25), target[1] - 4, lerp(target[2], end[2], 0.25)],
        end,
      ];
      vTop = 22;
      vBottom = lerp(40, 60, aggr);
    } else {
      const start: Vec3 = [target[0] + Math.cos(azIn) * 110, target[1] + 8, target[2] + Math.sin(azIn) * 110];
      const end: Vec3 = [target[0] + Math.cos(azOut) * 110, target[1] + 15, target[2] + Math.sin(azOut) * 110];
      p = [
        start,
        [lerp(start[0], target[0], 0.6), lerp(start[1], target[1], 0.6), lerp(start[2], target[2], 0.6)],
        [lerp(target[0], end[0], 0.3), target[1] + 2, lerp(target[2], end[2], 0.3)],
        end,
      ];
      vTop = 25;
      vBottom = 30;
    }
    const { arc, length, bottomAt } = arcTable(p, target);
    const exitDir: Vec3 = [Math.cos(azOut), 0.4, Math.sin(azOut)];
    return { kind, p, arc, length, vTop, vBottom, bottomAt, radius: lerp(10, 16, aggr), exitDir };
  }

  // ---- live ------------------------------------------------------------------

  private stepLive(dt: number, ctx: PredatorContext): void {
    const t = this.liveTarget!;
    if (!this.wasLive) {
      // Appear at the finger, moving; the chase takes over from here.
      this.wasLive = true;
      this.pos = [t[0], t[1], t[2]];
      this.vel = [0, 0, 0];
      this.prevVel = [0, 0, 0];
      this.radius = 0;
      this.liveLeaveUntil = 0;
    }
    this.mode = 'live';
    const d: Vec3 = [t[0] - this.pos[0], t[1] - this.pos[1], t[2] - this.pos[2]];
    const dist = Math.hypot(d[0], d[1], d[2]);
    const wantSpeed = Math.min(LIVE_MAX_SPEED, dist * 6);
    const desired: Vec3 = dist > 0.05 ? [(d[0] / dist) * wantSpeed, (d[1] / dist) * wantSpeed, (d[2] / dist) * wantSpeed] : [0, 0, 0];
    const k = 1 - Math.exp(-dt * 6);
    let nv: Vec3 = [this.vel[0] + (desired[0] - this.vel[0]) * k, this.vel[1] + (desired[1] - this.vel[1]) * k, this.vel[2] + (desired[2] - this.vel[2]) * k];
    // Turn limit, so a swipe becomes an arc rather than a teleport.
    const sp0 = Math.hypot(this.vel[0], this.vel[1], this.vel[2]);
    const sp1 = Math.hypot(nv[0], nv[1], nv[2]);
    if (sp0 > 1 && sp1 > 1) {
      const cosA = clamp((this.vel[0] * nv[0] + this.vel[1] * nv[1] + this.vel[2] * nv[2]) / (sp0 * sp1), -1, 1);
      const ang = Math.acos(cosA);
      const maxA = LIVE_MAX_TURN * dt;
      if (ang > maxA) {
        const f = maxA / ang;
        const mixed: Vec3 = [
          this.vel[0] / sp0 + (nv[0] / sp1 - this.vel[0] / sp0) * f,
          this.vel[1] / sp0 + (nv[1] / sp1 - this.vel[1] / sp0) * f,
          this.vel[2] / sp0 + (nv[2] / sp1 - this.vel[2] / sp0) * f,
        ];
        const ml = Math.hypot(mixed[0], mixed[1], mixed[2]) || 1;
        nv = [(mixed[0] / ml) * sp1, (mixed[1] / ml) * sp1, (mixed[2] / ml) * sp1];
      }
    }
    this.vel = nv;
    this.pos = [this.pos[0] + nv[0] * dt, Math.max(3, this.pos[1] + nv[1] * dt), this.pos[2] + nv[2] * dt];
    this.radius = approach(this.radius, lerp(10, 16, ctx.aggression), dt, 0.25);
  }

  // ---- startles --------------------------------------------------------------

  private stepStartles(simTime: number, dt: number, ctx: PredatorContext): void {
    const on = ctx.nervousness > 0.05;
    if (!on) {
      this.lastNerv = -1;
      this.nextStartle = Number.POSITIVE_INFINITY;
      return;
    }
    if (this.lastNerv !== ctx.nervousness) {
      this.lastNerv = ctx.nervousness;
      this.nextStartle = simTime + this.startleGap(ctx.nervousness) * (this.startleIndex === 0 ? 0.5 : 1);
    }
    if (simTime >= this.nextStartle) {
      const i = this.startleIndex++;
      const flockR = Math.max(8, ctx.spread * 0.6);
      const az = unit(this.seed, i, 20) * Math.PI * 2;
      const el = (unit(this.seed, i, 21) - 0.5) * 0.8;
      this.startle = [
        ctx.center[0] + Math.cos(az) * Math.cos(el) * flockR * 0.8,
        Math.max(4, ctx.center[1] + Math.sin(el) * flockR * 0.4),
        ctx.center[2] + Math.sin(az) * Math.cos(el) * flockR * 0.8,
        4,
      ];
      this.startleUntil = simTime + 0.2;
      this.nextStartle = simTime + this.startleGap(ctx.nervousness);
    }
    void dt;
  }

  /** Seconds between point startles: 60 s at low nervousness down to 15 s, jittered ±40 %. */
  private startleGap(nerv: number): number {
    return lerp(60, 15, nerv) * (0.6 + 0.8 * unit(this.seed, this.startleIndex, 22));
  }

  // ---- sprite ----------------------------------------------------------------

  private updateRoll(dt: number, flying: boolean): void {
    const ax = (this.vel[0] - this.prevVel[0]) / dt;
    const az = (this.vel[2] - this.prevVel[2]) / dt;
    const hs = Math.hypot(this.vel[0], this.vel[2]);
    if (hs > 1 && flying) {
      const sx = this.vel[2] / hs;
      const sz = -this.vel[0] / hs;
      const lat = clamp(ax * sx + az * sz, -40, 40);
      this.roll += (Math.atan2(lat, 9.81) - this.roll) * (1 - Math.exp(-dt * 5));
    } else {
      this.roll *= Math.exp(-dt * 3);
    }
    this.prevVel = [this.vel[0], this.vel[1], this.vel[2]];
  }
}

// ---- geometry ------------------------------------------------------------------

function bezier3(p: [Vec3, Vec3, Vec3, Vec3], u: number): Vec3 {
  const v = 1 - u;
  const w0 = v * v * v;
  const w1 = 3 * v * v * u;
  const w2 = 3 * v * u * u;
  const w3 = u * u * u;
  return [
    w0 * p[0][0] + w1 * p[1][0] + w2 * p[2][0] + w3 * p[3][0],
    w0 * p[0][1] + w1 * p[1][1] + w2 * p[2][1] + w3 * p[3][1],
    w0 * p[0][2] + w1 * p[1][2] + w2 * p[2][2] + w3 * p[3][2],
  ];
}

function add(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

const ARC_N = 64;

/** Cumulative arc length at ARC_N + 1 samples, the total, and the fraction nearest `target`. */
function arcTable(p: [Vec3, Vec3, Vec3, Vec3], target: Vec3): { arc: Float64Array; length: number; bottomAt: number } {
  const arc = new Float64Array(ARC_N + 1);
  let prev = bezier3(p, 0);
  let best = Infinity;
  let bestAt = 0.5;
  for (let i = 1; i <= ARC_N; i++) {
    const q = bezier3(p, i / ARC_N);
    arc[i] = arc[i - 1]! + Math.hypot(q[0] - prev[0], q[1] - prev[1], q[2] - prev[2]);
    const d = Math.hypot(q[0] - target[0], q[1] - target[1], q[2] - target[2]);
    if (d < best) {
      best = d;
      bestAt = arc[i]!;
    }
    prev = q;
  }
  const length = arc[ARC_N]!;
  return { arc, length, bottomAt: length > 0 ? bestAt / length : 0.5 };
}

/** Curve parameter u for an arc-length fraction, by inverting the table. */
function arcToParam(arc: Float64Array, frac: number): number {
  const target = clamp(frac, 0, 1) * arc[ARC_N]!;
  let lo = 0;
  let hi = ARC_N;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (arc[mid]! < target) lo = mid;
    else hi = mid;
  }
  const a = arc[lo]!;
  const b = arc[hi]!;
  const t = b > a ? (target - a) / (b - a) : 0;
  return (lo + t) / ARC_N;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function smoothstep(e0: number, e1: number, x: number): number {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
}

/** Move `v` toward `target`, covering the full distance in about `seconds`. */
function approach(v: number, target: number, dt: number, seconds: number): number {
  const k = 1 - Math.exp(-dt * (3 / seconds));
  const n = v + (target - v) * k;
  return Math.abs(n - target) < 0.02 ? target : n;
}
